import React, { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Upload, Trash2, Search, Send, MessageSquare, Mail, Globe, RefreshCw, ChevronDown, ChevronUp, Pencil, X, Sparkles, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { ChatText } from "./inline";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full rounded-lg border border-[#26201625] bg-white/80 px-3.5 py-2.5 text-sm text-[#262016] outline-none focus:border-[#B5793F] placeholder-[#8A7E6B]";
const labelCls = "block text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] font-medium mb-1.5";
const STATUS_COLORS = {
  new: "#3B82F6", researched: "#8B5CF6", drafted: "#F59E0B",
  sent: "#EC4899", replied: "#10B981", converted: "#059669", lost: "#6B7280",
};

/* ── Pipeline badges ───────────────────────────────── */
function PipelineBadges({ stats }) {
  if (!stats) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-6" data-testid="outreach-pipeline">
      {Object.entries(stats.by_status || {}).map(([s, c]) => (
        <span key={s} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold"
              style={{ background: (STATUS_COLORS[s] || "#8A7E6B") + "18", color: STATUS_COLORS[s] || "#8A7E6B" }}>
          <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[s] || "#8A7E6B" }} />
          {s}: {c}
        </span>
      ))}
    </div>
  );
}

/* ── Contact Form ──────────────────────────────────── */
function ContactForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || { status: "new" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const isEdit = Boolean(initial?.id);
      const r = await fetch(`${API}/api/admin/outreach/contacts${isEdit ? `/${initial.id}` : ""}`, {
        method: isEdit ? "PUT" : "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error("Save failed");
      toast.success(isEdit ? "Contact updated" : "Contact added");
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="rp-foam bg-white/70 p-6 md:p-8 mb-6" data-testid="contact-form">
      <div className="flex items-center justify-between mb-5">
        <p className="rp-display text-xl font-medium text-[#262016]">{initial?.id ? "Edit contact" : "Add contact"}</p>
        <button onClick={onCancel} className="text-[#8A7E6B] hover:text-[#262016]"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className={labelCls}>Name *</label>
          <input value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Contact name" className={inputCls} data-testid="contact-name" /></div>
        <div><label className={labelCls}>Company</label>
          <input value={form.company || ""} onChange={(e) => set("company", e.target.value)} placeholder="Business name" className={inputCls} data-testid="contact-company" /></div>
        <div><label className={labelCls}>Phone (WhatsApp)</label>
          <input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98765 43210" className={inputCls} data-testid="contact-phone" /></div>
        <div><label className={labelCls}>Email</label>
          <input value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="email@company.com" className={inputCls} data-testid="contact-email" /></div>
        <div><label className={labelCls}>Website</label>
          <input value={form.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://..." className={inputCls} data-testid="contact-website" /></div>
        <div><label className={labelCls}>Industry</label>
          <input value={form.industry || ""} onChange={(e) => set("industry", e.target.value)} placeholder="e.g. Bakery, Fashion" className={inputCls} data-testid="contact-industry" /></div>
        <div><label className={labelCls}>Location</label>
          <input value={form.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="City" className={inputCls} data-testid="contact-location" /></div>
        <div><label className={labelCls}>Instagram</label>
          <input value={form.instagram || ""} onChange={(e) => set("instagram", e.target.value)} placeholder="@handle" className={inputCls} /></div>
        <div><label className={labelCls}>LinkedIn</label>
          <input value={form.linkedin || ""} onChange={(e) => set("linkedin", e.target.value)} placeholder="Profile URL" className={inputCls} /></div>
        <div><label className={labelCls}>Facebook</label>
          <input value={form.facebook || ""} onChange={(e) => set("facebook", e.target.value)} placeholder="Page URL" className={inputCls} /></div>
        <div className="md:col-span-2"><label className={labelCls}>Business context (what they do, what you know about them)</label>
          <textarea value={form.context || ""} onChange={(e) => set("context", e.target.value)} rows={3} placeholder="e.g. They run a bakery in Pune, have decent Instagram but weak branding..." className={inputCls} data-testid="contact-context" /></div>
      </div>
      <button onClick={save} disabled={busy} data-testid="contact-save-btn"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
        {busy ? "Saving..." : "Save"}
      </button>
    </div>
  );
}

/* ── Contact Card (expanded) ───────────────────────── */
function ContactCard({ c, onEdit, onDelete, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [researching, setResearching] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [tone, setTone] = useState("helpful");
  const [customInstr, setCustomInstr] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const doResearch = async () => {
    setResearching(true);
    try {
      const r = await fetch(`${API}/api/admin/outreach/contacts/${c.id}/research`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      toast.success("Research complete");
      onRefresh();
    } catch (e) { toast.error(e.message); }
    finally { setResearching(false); }
  };

  const doDraft = async (channel) => {
    setDrafting(channel);
    try {
      const r = await fetch(`${API}/api/admin/outreach/contacts/${c.id}/draft`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ channel, tone, custom_instructions: customInstr }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      toast.success(`${channel === "whatsapp" ? "WhatsApp" : "Email"} draft ready`);
      onRefresh();
    } catch (e) { toast.error(e.message); }
    finally { setDrafting(null); }
  };

  const sendEmail = async () => {
    if (!c.email) return toast.error("No email address");
    if (!c.ai_email_draft) return toast.error("Draft an email first");
    if (!window.confirm(`Send email to ${c.email}?`)) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/admin/outreach/contacts/${c.id}/send-email`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ subject: c.ai_email_subject || "Hello from Rosh", body: c.ai_email_draft }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      toast.success(`Email sent to ${c.email}`);
      onRefresh();
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const whatsappLink = () => {
    if (!c.phone) return null;
    const phone = c.phone.replace(/[^0-9+]/g, "").replace(/^\+/, "");
    const text = encodeURIComponent(c.ai_whatsapp_draft || "");
    return `https://wa.me/${phone}?text=${text}`;
  };

  const copyDraft = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const statusColor = STATUS_COLORS[c.status] || "#8A7E6B";

  return (
    <div className="bg-white rounded-2xl border border-[#d4cbb8] shadow-sm" style={{ marginBottom: "20px" }} data-testid={`contact-${c.id}`}>
      <div className="flex items-center gap-4 px-5 py-4 cursor-pointer" onClick={() => setExpanded((e) => !e)}>
        <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white text-sm font-bold"
              style={{ background: statusColor }}>
          {(c.name || "?")[0].toUpperCase()}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-[#262016]">{c.name}</p>
            {c.company && <span className="text-[12px] text-[#8A7E6B]">— {c.company}</span>}
            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                  style={{ background: statusColor + "18", color: statusColor }}>{c.status}</span>
          </div>
          <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-[#8A7E6B]">
            {c.phone && <span>{c.phone}</span>}
            {c.email && <span>{c.email}</span>}
            {c.industry && <span>{c.industry}</span>}
            {c.location && <span>{c.location}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onEdit(c); }} className="w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-[#262016]" data-testid={`contact-edit-${c.id}`}>
            <Pencil size={13} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(c.id); }} className="w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-red-700" data-testid={`contact-delete-${c.id}`}>
            <Trash2 size={13} />
          </button>
          {expanded ? <ChevronUp size={16} className="text-[#8A7E6B]" /> : <ChevronDown size={16} className="text-[#8A7E6B]" />}
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 border-t border-[#26201614] pt-4 space-y-5">
          {c.context && <div><p className={labelCls}>Context</p><p className="text-sm text-[#5C5042]">{c.context}</p></div>}

          {/* AI Research */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className={labelCls}>AI Research</p>
              <button onClick={doResearch} disabled={researching} data-testid={`contact-research-${c.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium text-white bg-[#B5793F] hover:bg-[#9A6735] disabled:opacity-50">
                {researching ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {researching ? "Researching..." : c.ai_research ? "Re-research" : "Research business"}
              </button>
            </div>
            {c.ai_research && (
              <div className="bg-[#262016]/5 rounded-xl p-4 text-sm text-[#3B301F] leading-relaxed">
                <ChatText text={c.ai_research} />
              </div>
            )}
          </div>

          {/* Draft controls */}
          <div>
            <p className={labelCls}>Draft message</p>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <select value={tone} onChange={(e) => setTone(e.target.value)} className="rounded-lg border border-[#26201625] bg-white/80 px-3 py-2 text-[12px] text-[#262016] outline-none">
                <option value="helpful">Helpful (value-first)</option>
                <option value="promotional">Promotional (subtle)</option>
                <option value="follow_up">Follow-up</option>
              </select>
              <button onClick={() => doDraft("whatsapp")} disabled={!!drafting} data-testid={`contact-draft-wa-${c.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-medium border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 disabled:opacity-50">
                {drafting === "whatsapp" ? <RefreshCw size={12} className="animate-spin" /> : <MessageSquare size={12} />}
                Draft WhatsApp
              </button>
              <button onClick={() => doDraft("email")} disabled={!!drafting} data-testid={`contact-draft-email-${c.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-medium border border-[#B5793F] text-[#B5793F] hover:bg-[#B5793F]/10 disabled:opacity-50">
                {drafting === "email" ? <RefreshCw size={12} className="animate-spin" /> : <Mail size={12} />}
                Draft Email
              </button>
            </div>
            <input value={customInstr} onChange={(e) => setCustomInstr(e.target.value)} placeholder="Custom instructions (optional) — e.g. mention their new product launch"
                   className={inputCls + " mb-3"} />
          </div>

          {/* WhatsApp draft */}
          {c.ai_whatsapp_draft && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className={labelCls}>WhatsApp draft</p>
                <div className="flex gap-2">
                  <button onClick={() => copyDraft(c.ai_whatsapp_draft)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-[#8A7E6B] hover:text-[#262016]">
                    {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "Copied" : "Copy"}
                  </button>
                  {whatsappLink() && (
                    <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid={`contact-send-wa-${c.id}`}
                       className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium text-white bg-[#25D366] hover:bg-[#1DA851]">
                      <Send size={12} /> Open WhatsApp
                    </a>
                  )}
                </div>
              </div>
              <div className="bg-[#dcf8c6] rounded-xl p-4 text-sm text-[#262016] whitespace-pre-wrap">{c.ai_whatsapp_draft}</div>
            </div>
          )}

          {/* Email draft */}
          {c.ai_email_draft && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className={labelCls}>Email draft {c.ai_email_subject && <span className="normal-case tracking-normal text-[#262016]">— {c.ai_email_subject}</span>}</p>
                <div className="flex gap-2">
                  <button onClick={() => copyDraft(c.ai_email_draft)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-[#8A7E6B] hover:text-[#262016]">
                    {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "Copied" : "Copy"}
                  </button>
                  <button onClick={sendEmail} disabled={sending || !c.email} data-testid={`contact-send-email-${c.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium text-white bg-[#B5793F] hover:bg-[#9A6735] disabled:opacity-50">
                    {sending ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                    {sending ? "Sending..." : "Send email"}
                  </button>
                </div>
              </div>
              <div className="bg-[#f0eee8] rounded-xl p-4 text-sm text-[#262016] whitespace-pre-wrap">{c.ai_email_draft}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Email Finder ──────────────────────────────────── */
function EmailFinder() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setResults(null);
    try {
      const r = await fetch(`${API}/api/admin/outreach/email-finder`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ query }),
      });
      if (!r.ok) throw new Error("Search failed");
      setResults(await r.json());
    } catch (e) { toast.error(e.message); }
    finally { setSearching(false); }
  };

  return (
    <div className="rp-foam bg-white/70 p-6 mb-6" data-testid="email-finder">
      <p className="rp-display text-lg font-medium text-[#262016] mb-3">Find emails</p>
      <p className="text-sm text-[#5C5042] mb-4">Enter a website URL, company name, or social profile to find email addresses.</p>
      <div className="flex gap-3">
        <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
               placeholder="e.g. sweetbites.in or 'Sweet Bites Bakery Mumbai'" className={inputCls + " flex-1"} data-testid="email-finder-input" />
        <button onClick={search} disabled={searching} data-testid="email-finder-search-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5] shrink-0 disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
          {searching ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
          {searching ? "Searching..." : "Find"}
        </button>
      </div>
      {results && (
        <div className="mt-4" data-testid="email-finder-results">
          {results.total === 0 ? (
            <p className="text-sm text-[#8A7E6B] italic">No emails found for "{results.query}". Try a direct website URL.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium text-[#262016]">{results.total} email{results.total > 1 ? "s" : ""} found:</p>
              {results.emails.map((e, i) => (
                <div key={i} className="flex items-center gap-3 bg-[#262016]/5 rounded-lg px-4 py-2.5">
                  <Mail size={14} className="text-[#B5793F] shrink-0" />
                  <span className="text-sm font-medium text-[#262016] flex-1">{e.email}</span>
                  <span className="text-[11px] text-[#8A7E6B] truncate max-w-[200px]">{e.source}</span>
                  <button onClick={() => { navigator.clipboard.writeText(e.email); toast.success("Copied!"); }}
                          className="text-[#8A7E6B] hover:text-[#262016]"><Copy size={13} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main Outreach Section ─────────────────────────── */
export default function OutreachSection() {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const fileRef = useRef(null);

  const load = useCallback(() => {
    fetch(`${API}/api/admin/outreach/contacts`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : [])).then(setContacts).catch(() => setContacts([]));
    fetch(`${API}/api/admin/outreach/stats`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then(setStats).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    const r = await fetch(`${API}/api/admin/outreach/contacts/${id}`, { method: "DELETE", credentials: "include" });
    if (r.ok) { toast.success("Deleted"); load(); } else toast.error("Delete failed");
  };

  const importCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const r = await fetch(`${API}/api/admin/outreach/contacts/import-csv`, {
        method: "POST", credentials: "include", body: formData,
      });
      if (!r.ok) throw new Error("Import failed");
      const d = await r.json();
      toast.success(`Imported ${d.imported} contacts`);
      load();
    } catch (err) { toast.error(err.message); }
    e.target.value = "";
  };

  const filtered = filterStatus === "all" ? contacts : contacts.filter((c) => c.status === filterStatus);

  return (
    <div data-testid="outreach-section">
      <PipelineBadges stats={stats} />

      {/* Email finder */}
      <EmailFinder />

      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {editing === null && (
          <>
            <button onClick={() => setEditing({ _new: true })} data-testid="outreach-add-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]"
                    style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
              <Plus size={15} /> Add contact
            </button>
            <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-[#26201625] text-[#262016] cursor-pointer hover:border-[#B5793F]"
                   data-testid="outreach-import-btn">
              <Upload size={15} /> Import CSV
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCSV} />
            </label>
          </>
        )}
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="outreach-filter"
                className="ml-auto rounded-lg border border-[#26201625] bg-white/80 px-3 py-2 text-[12px] text-[#262016] outline-none">
          <option value="all">All ({contacts.length})</option>
          {["new", "researched", "drafted", "sent", "replied", "converted", "lost"].map((s) => (
            <option key={s} value={s}>{s} ({contacts.filter((c) => c.status === s).length})</option>
          ))}
        </select>
      </div>

      {/* Contact form */}
      {editing && (
        <ContactForm initial={editing._new ? null : editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
      )}

      {/* Contact list */}
      <div data-testid="outreach-contacts-list">
        {filtered.length === 0 && !editing && (
          <p className="rp-foam bg-white/45 p-8 text-center text-sm text-[#8A7E6B]">
            No contacts yet. Add manually or import a CSV with columns: name, phone, email, company, website, industry, location, context.
          </p>
        )}
        {filtered.map((c) => (
          <ContactCard key={c.id} c={c} onEdit={setEditing} onDelete={deleteContact} onRefresh={load} />
        ))}
      </div>
    </div>
  );
}
